import { toast } from "sonner";

// Map of Supabase error codes to user-friendly messages
const ERROR_MESSAGES: Record<string, string> = {
  "23505": "This record already exists",
  "23503": "This operation would break a relationship with another record",
  "23502": "A required field is missing",
  "42501": "You don't have permission to perform this action",
  "PGRST116": "No records found",
  "PGRST301": "Too many requests. Please try again later",
  "invalid_credentials": "Invalid email or password",
  "email_not_confirmed": "Please confirm your email before signing in",
  "user_already_exists": "An account with this email already exists",
  "weak_password": "Password is too weak. Use at least 6 characters",
  "rate_limit_exceeded": "Too many attempts. Please try again later",
  "invalid_email": "Please enter a valid email address",
};

/**
 * Handles errors by logging them server-side and showing user-friendly messages
 * This prevents exposing sensitive database/system information to users
 */
export const handleError = (error: any, fallbackMessage: string = "Something went wrong") => {
  // Generate unique error reference ID for support/debugging
  const errorId = crypto.randomUUID().substring(0, 8);
  
  // Extract error details
  const errorMessage = error?.message || "";
  const errorCode = error?.code || "";

  // Log full error details for debugging (not visible to users)
  console.error(`Error [${errorId}]:`, {
    message: errorMessage,
    code: errorCode,
    details: error?.details,
    hint: error?.hint,
    fullError: error
  });

  // Find a user-friendly message
  let userMessage = fallbackMessage;
  
  // Check for known error codes
  if (errorCode && ERROR_MESSAGES[errorCode]) {
    userMessage = ERROR_MESSAGES[errorCode];
  }
  // Check for auth-related errors
  else if (errorMessage.toLowerCase().includes("invalid login")) {
    userMessage = "Invalid email or password";
  }
  else if (errorMessage.toLowerCase().includes("email not confirmed")) {
    userMessage = "Please confirm your email before signing in";
  }
  else if (errorMessage.toLowerCase().includes("already registered") || 
           errorMessage.toLowerCase().includes("already exists")) {
    userMessage = "An account with this email already exists";
  }
  else if (errorMessage.toLowerCase().includes("rate limit")) {
    userMessage = "Too many attempts. Please try again later";
  }
  else if (errorMessage.toLowerCase().includes("network") || 
           errorMessage.toLowerCase().includes("fetch")) {
    userMessage = "Network error. Please check your connection";
  }
  else if (errorMessage.toLowerCase().includes("permission") || 
           errorMessage.toLowerCase().includes("unauthorized")) {
    userMessage = "You don't have permission to perform this action";
  }
  else if (errorMessage.toLowerCase().includes("not found")) {
    userMessage = "The requested resource was not found";
  }

  // Show toast notification with reference ID
  toast.error(`${userMessage} (Ref: ${errorId})`);

  return errorId;
};

export const handleApiError = async (response: Response, fallbackMessage: string) => {
  try {
    const data = await response.json();
    return handleError(data, fallbackMessage);
  } catch {
    return handleError({ message: fallbackMessage }, fallbackMessage);
  }
};
